/**
 * 整箱询价管理前端接口
 */

import { request } from '@/utils/http'
import { type BatchDeleteRequest, type PageResponse } from '@/api/types/common'
import {
  type InquiryBaseModel,
  type InquiryPageRequest,
  type InquirySaveRequest
} from './inquiry-base'

/**
 * 询报价 V2 列表中的报价行（quotationType: 1供应商 / 2商务 / 3销售）
 */
export interface InquiryQuoteListItem {
  id?: string | number
  quotationNo?: string
  quotationType?: number
  status?: number | null
  consultationStatus?: number | null
  orderIntention?: number | null
  carrierId?: string | number | null
  sellingPrice?: string | null
  submitDate?: string | null
  dateCreated?: string | null
  dateModified?: string | null
  contactName?: string | null
  supplierName?: string | null
  stuffingDetailAddress?: string | null
  stuffingPickupPortId?: string | number | null
  preLoadingPortId?: string | number | null
  porId?: string | number | null
  portId?: string | number | null
  loadingPortId?: string | number | null
  dischargingPortId?: string | number | null
  destinationPortId?: string | number | null
  transferPortIds?: Array<string | number> | null
  transferPortInfo?: Array<string | number> | null
  transferPortId1?: string | number | null
  transferPortId2?: string | number | null
  transferPortId3?: string | number | null
  seaEq?: string | null
  remarks?: string | null
  supplierRemarks?: string | null
  supplierId?: string | number | null
  quoterId?: string | number | null
  createdBy?: string | number | null
  [key: string]: unknown
}

/**
 * 整箱询价模型（继承基础询价模型；V2 /page 额外带回三角色报价列表）
 */
export interface InquiryFclModel extends InquiryBaseModel {
  /** 报价截止时间（FCL V2 字段，优先于 deadLine） */
  quotationDeadline?: string | null
  /** 询价人名称（可手填或取自委托方提交人员） */
  inquiryName?: string | null
  inquiryPhone?: string | null
  inquiryEmail?: string | null
  /** 起运国/城市、目的国/城市（FCL） */
  loadingCountryId?: string | number | null
  loadingCityId?: string | number | null
  destinationCountryId?: string | number | null
  destinationCityId?: string | number | null
  stuffingAddressType?: number | null
  stuffingPickupPortId?: string | number | null
  stuffingPickupTerminalId?: string | number | null
  stuffingTerminalNameCn?: string | null
  stuffingTerminalNameEn?: string | null
  deliveryTerminalNameCn?: string | null
  deliveryTerminalNameEn?: string | null
  loadingAirportId?: string | number | null
  destinationAirportId?: string | number | null
  loadingRailwayStationId?: string | number | null
  destinationStationId?: string | number | null
  /** 船公司 ID 集合（FCL 多选，优先于单值 carrierId） */
  carrierIds?: Array<string | number> | null
  /** 服务类型 FCL/LCL/RORO/BULK */
  serviceType?: string | null
  /** 销售报价列表 quotationType=3 */
  salesQuotationList?: InquiryQuoteListItem[] | null
  /** 商务报价列表 quotationType=2 */
  clientQuotationList?: InquiryQuoteListItem[] | null
  /** 供应商报价列表 quotationType=1 */
  supplierQuotationList?: InquiryQuoteListItem[] | null
  /** 销售报价状态 0-无 1-待报价 2-已报价 */
  salesQuotationStatus?: number | null
  /** 商务报价状态 0-无 1-待报价 2-已报价（后端字段；兼容旧 clientQuotationStatus） */
  businessQuotationStatus?: number | null
  /** @deprecated 已更名为 businessQuotationStatus，保留兼容旧接口 */
  clientQuotationStatus?: number | null
  /** 供应商报价状态 0-无 1-待报价 2-已报价 */
  supplierQuotationStatus?: number | null
  salesQuotationCount?: number | null
  clientQuotationCount?: number | null
  supplierQuotationCount?: number | null
  /** 销售报价人（向商务询价时写入） */
  salesQuoterId?: string | number | null
  /** 商务报价人 ID 列表（向商务询价时写入） */
  businessQuoterIds?: Array<string | number> | null
  /** 报价供应商企业 ID 列表（列表 VO 聚合自供应商报价 supplierId） */
  inquiryTargetIds?: Array<string | number> | null
  /** 已发送供应商询价（详情/部分接口带回） */
  supplierInquiryList?: Array<Record<string, unknown>> | null
}

/**
 * 整箱询价分页查询请求参数
 */
export interface InquiryFclPageRequest extends InquiryPageRequest {
}

/**
 * 整箱询价保存请求参数
 */
export interface InquiryFclSaveRequest extends InquirySaveRequest {
}

/** 整箱询价 V2 前缀（InquiryFclControllerV2） */
const FCL_V2 = '/price/inquiry/fcl/v2'

/**
 * 分页查询整箱询价列表（询报价 V2：含销售/商务/供应商报价聚合）
 */
export const pageInquiryFcl = (params: InquiryFclPageRequest) => {
  return request.post<PageResponse<InquiryFclModel>>(`${FCL_V2}/page`, params)
}

/**
 * 根据ID获取整箱询价详情
 */
export const getInquiryFclById = (id: string) => {
  return request.post<InquiryFclModel>(`${FCL_V2}/get`, { id })
}

/**
 * 根据询价编号获取整箱询价详情
 */
export const getInquiryFclByInquiryNo = (inquiryNo: string) => {
  return request.post<InquiryFclModel>(`${FCL_V2}/get`, { inquiryNo })
}

/**
 * 新增整箱询价（V2 返回新建询价 ID）
 */
export const saveInquiryFcl = (params: InquiryFclSaveRequest) => {
  return request.post<number | string>(`${FCL_V2}/save`, params)
}

/**
 * 修改整箱询价
 */
export const updateInquiryFcl = (params: InquiryFclSaveRequest) => {
  return request.post<boolean>(`${FCL_V2}/update`, params)
}

/**
 * 批量删除整箱询价
 */
export const batchDeleteInquiryFcl = (params: BatchDeleteRequest) => {
  return request.post<boolean>(`${FCL_V2}/batch-delete`, params)
}

/**
 * 更新已确认报价单ID
 */
export const updateConfirmedQuotation = (params: { id: string; confirmedQuotationId: string | null }) => {
  return request.post<boolean>(`${FCL_V2}/update-confirmed-quotation`, params)
}
